import React from 'react';
import { css, styled } from '../../../common.styles';
import { marginInline } from '../../../styles/general/logical-properties';
import {
  baseColor,
  normalFont,
  mobile,
  primaryColor,
  primaryFont,
  secondaryColor,
  secondaryFont,
} from '../../../styles/general/theme';
import { LazyFaIcon } from '../../icon/general/lazy-fa-icon';
import { BasicButtonStyle, ButtonProps, SecondaryButtonWithDisabledStyle } from '../general/common-button';
import { useAsset } from '../../../assets/peugeot/peu-use-asset';

export const BaseSimpleButtonWithDisabledStyle = css<{ disabled?: boolean | undefined }>`
  box-sizing: border-box;
  font-size: 14px;
  line-height: 16px;
  font-family: ${primaryFont};
  font-weight: bold;
  padding: 13px 15px;
  ${SecondaryButtonWithDisabledStyle};
`;

const PeuArrowAndTextButtonWrapper = styled.a<{
  disabled?: boolean | undefined;
  darkBackground?: boolean;
  fullWidth?: boolean;
  darkMode?: boolean;
}>`
  font-family: ${primaryFont};
  font-size: 14px;
  font-style: normal;
  font-weight: 300;
  line-height: 22px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${(p) => (p.darkBackground ? baseColor(900) : baseColor(100))};
  box-sizing: border-box;
  &:hover {
    font-weight: 700;
  }

  ${(p) =>
    p.disabled &&
    css`
      cursor: not-allowed;
      color: ${baseColor(560)};
      font-weight: 300;
      &:hover {
        color: ${baseColor(560)};
        font-weight: 300;
      }
    `};

  ${(p) =>
    p.darkMode &&
    css`
      color: ${baseColor(900)};
      text-decoration: none;

      &:hover {
        text-decoration: none;
        font-family: ${normalFont};
        font-weight: 400;
        color: ${baseColor(900)};
      }
    `};
`;

const PeuTextButtonWrapper = styled(PeuArrowAndTextButtonWrapper)`
  justify-content: center;
`;

export const SimpleButtonStyle = css`
  ${BasicButtonStyle};
  padding-left: 36px;
  padding-right: 36px;
  box-sizing: border-box;
  height: 46px;
  text-align: center;
  font-family: ${(p) => p.theme.font.accent};
  font-size: ${(p) => p.theme.fontSize.small}px;
  color: ${baseColor(900)};
  background-color: ${primaryColor(200)};
  cursor: pointer;
  min-width: 180px;

  :visited {
    color: ${baseColor(900)};
  }

  :hover {
    background-color: ${baseColor(100)};
    color: ${baseColor(900)};
  }
`;

export const SimpleButton = styled.a<{ disabled?: boolean; darkMode?: boolean }>`
  ${SimpleButtonStyle};
  color: ${baseColor(900)};
  background-color: ${primaryColor(200)};

  ${(p) =>
    p.disabled &&
    css`
      color: ${baseColor(900)};
      background-color: ${baseColor(500)};
      cursor: not-allowed;
    `};

  :visited {
    color: ${baseColor(900)};
  }

  :hover {
    background-color: ${baseColor(500)};
    color: ${baseColor(900)};
  }

  ${(p) =>
    p.darkMode &&
    css`
      &:hover {
        background-color: ${baseColor(100)};
        border-color: ${baseColor(100)};
        color: ${baseColor(900)};
      }
    `};

  @media (max-width: ${mobile}px) {
    width: 100%;
  }
`;

export const SimpleButtonWithDisabledStyle = css<{ disabled?: boolean | undefined }>`
  ${BasicButtonStyle};
  ${BaseSimpleButtonWithDisabledStyle};
  background-color: ${(p) => (p.disabled ? p.theme.color.base[750] : p.theme.color.primary[300])};
  border: 4px solid ${(p) => (p.disabled ? p.theme.color.base[750] : p.theme.color.primary[300])};
  color: ${(p) => p.theme.color.base[900]};
  max-width: 285px;
  height: 46px;
  text-align: center;

  :visited {
    color: ${(p) => p.theme.color.base[900]};
  }

  :hover {
    background-color: ${(p) => p.theme.color.base[900]};
    border: 4px solid ${(p) => p.theme.color.primary[300]};
    color: ${(p) => p.theme.color.primary[300]};

    > * {
      color: ${(p) => p.theme.color.primary[300]};
    }
  }
`;

export const SimpleButtonWithDisabled = styled.a<{ disabled?: boolean | undefined }>`
  ${SimpleButtonWithDisabledStyle};
`;

export const PrimaryButton = (props: ButtonProps) => {
  return (
    <SimpleButton
      {...props}
      onClick={props.onClick}
      href={props.href}
      target={props.target}
      className={props.className}
    >
      {props.children}
    </SimpleButton>
  );
};

export const SecondaryButton = styled(PrimaryButton)`
  color: ${secondaryColor(300)};
  font-size: ${(p) => p.theme.fontSize.small};
  font-family: ${secondaryFont};
  border: 1px solid ${secondaryColor(300)};
  background: none;

  :visited {
    color: ${secondaryColor(300)};
  }

  :hover {
    background-color: ${primaryColor(200)};
    color: ${baseColor(900)};
    border-color: ${primaryColor(200)};
  }

  ${(p) =>
    p.darkMode &&
    css`
      background-color: transparent;
      border-color: ${baseColor(900)};
      color: ${baseColor(900)};

      :visited {
        color: ${baseColor(900)};
      }
    `};
`;

export const PeuTextButton: React.FC<ButtonProps> = (props) => {
  return <PeuTextButtonWrapper {...props}>{props.children}</PeuTextButtonWrapper>;
};

const Svg = styled.svg`
  width: 24px;
  height: 24px;
  path {
    fill: currentColor;
    transition: fill 0.2s ease;
  }
`;

export const PeuChevronRightArrowIcon: React.FC = () => (
  <Svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.816 7.456L8.832 8.456L12.816 12L8.832 15.544L9.856 16.544L15.184 12L9.816 7.456Z" />
  </Svg>
);

export const PeuChevronLeftArrowIcon: React.FC = () => (
  <Svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ transform: 'scaleX(-1)' }}>
    <path d="M9.816 7.456L8.832 8.456L12.816 12L8.832 15.544L9.856 16.544L15.184 12L9.816 7.456Z" />
  </Svg>
);

export const PeuArrowAndTextButton: React.FC<ButtonProps> = (props) => {
  const position = props.iconPosition || 'left';
  return (
    <PeuArrowAndTextButtonWrapper {...props}>
      {position === 'left' && <PeuChevronLeftArrowIcon />}
      {props.children}
      {position === 'right' && <PeuChevronRightArrowIcon />}
    </PeuArrowAndTextButtonWrapper>
  );
};

export const PeuButton = (props: ButtonProps): any => {
  switch (props?.variant?.toLowerCase()) {
    case 'primary':
      return <PrimaryButton {...props} />;
    case 'secondary':
      return <SecondaryButton {...props} />;
    case 'tertiary':
      return <PeuTextButton {...props} />;
    case 'icon':
      return <PeuArrowAndTextButton {...props} />;
  }
};

export const SecondaryButtonStyle = css`
  ${SimpleButtonStyle};
  color: ${secondaryColor(300)};
  font-size: ${(p) => p.theme.fontSize.small};
  font-family: ${secondaryFont};
  border: 1px solid ${secondaryColor(300)};
  background: none;

  :visited {
    color: ${secondaryColor(300)};
  }

  :hover {
    background-color: ${primaryColor(200)};
    color: ${baseColor(900)};
    border-color: ${primaryColor(200)};
  }
`;

export const FullWidthSecondaryButton = styled(SecondaryButton)`
  width: 100%;
  margin-bottom: 20px;
  margin-top: 10px;
`;

export const BackButtonElementContainer = styled(SimpleButton)`
  display: flex;
  align-items: center;
  font-size: ${(p) => p.theme.fontSize.small};
  font-family: ${primaryFont};
  font-weight: 600;
  background-color: ${(p) => p.theme.color.base[900]};
  color: ${(p) => p.theme.color.base[300]};
  border: 1px solid ${(p) => p.theme.color.base[400]};
  line-height: 1em;
  padding: 15px;

  :visited {
    color: ${(p) => p.theme.color.base[300]};
  }

  :hover {
    background-color: ${(p) => p.theme.color.base[900]};
    border: 1px solid ${(p) => p.theme.color.base[100]};
  }
`;

export const FullPageWidthBackButtonElementContainer = styled(SimpleButton)`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-size: ${(p) => p.theme.fontSize.small};
  font-family: ${primaryFont};
  font-weight: 600;
  background-color: ${baseColor(700)};
  color: ${baseColor(100)};
  line-height: 1em;
  padding: 15px;
  max-width: 100%;
  width: 100%;

  :hover {
    background-color: ${baseColor(700)};
  }

  :visited {
    color: ${baseColor(100)};
  }
`;

export const PeuChevron = styled(LazyFaIcon)`
  color: ${(p) => p.theme.color.primary[550]};
  ${marginInline(0, 8)};
  font-size: ${(p) => p.theme.fontSize.normal}px;
`;

export const FullPagePeuBackButton = (props: ButtonProps) => {
  const icon = useAsset('backArrowIcon');
  return (
    <FullPageWidthBackButtonElementContainer
      onClick={props.onClick}
      href={props.href}
      className={props.className}
      {...props}
    >
      <img src={icon} />
      {props.children}
    </FullPageWidthBackButtonElementContainer>
  );
};
